/* eslint-disable class-methods-use-this, no-console */
import api from '@cannect/services/api'

export interface PaymentChargePayload {
  order_id: number
  payments: Array<{
    paymentMethod: { type: 'pix' | 'boleto' | 'credit_card' }
    amount: number
    card_id?: string | number
    cvv?: string
    card_number?: string
    card_name?: string
    card_month_validate?: string
    card_year_validate?: string
    card_cvv?: string
    installments?: number
    remeberCard?: boolean
    card_value?: number
  }>
  utmParams: Record<string, any>
}

export interface PixTransaction {
  id: string
  gateway_id: string
  qr_code: string
  qr_code_text: string
  qr_code_url: string
  expires_at: string
  status: string
  pix_provider_tid: string
}

export interface BoletoTransaction {
  id: string
  gateway_id: string
  // Compat: campos antigos
  boleto_barcode?: string
  boleto_expiration_date?: string
  boleto_url?: string
  // Novos campos do provider
  line?: string
  pdf?: string
  barcode?: string
  url?: string
  due_at?: string
  status: string
  qr_code?: string
  qr_code_url?: string
}

export interface PagaleveCheckout {
  checkout_id: string
  checkout_url: string
  qr_code: string
  qr_code_url: string
  status: string
  expires_at: string
  amount: number
}

export interface PagaleveEligibilityRequest {
  cpf: string
  amount: number
  phone: string
  email: string
}

export interface PagaleveEligibilityResponse {
  success: boolean
  data?: {
    eligible: boolean
    action: string
    message: string
    details?: any
  }
  message?: string
}

export interface PaymentResponse {
  success: boolean
  responses: Array<{
    id: string
    code: string
    gateway_id?: string
    amount: number
    status: string
    currency: string
    payment_method: string
    created_at: string
    updated_at: string
    order: {
      id: string
      code: string
      amount: number
      status: string
      currency: string
      customer_id: string
      items: Array<{
        id: string
        description: string
        amount: number
        quantity: number
        status: string
        code: string
      }>
    }
    customer: {
      id: string
      name: string
      email: string
      code: string
      document: string
      type: string
    }
    last_transaction: {
      id: string
      transaction_type: string
      amount: number
      status: string
      success: boolean
      expires_at?: string
      // Boleto (novos campos)
      url?: string
      pdf?: string
      line?: string
      barcode?: string
      due_at?: string
      qr_code?: string
      qr_code_text?: string
      qr_code_url?: string
      boleto_barcode?: string
      boleto_expiration_date?: string
      boleto_url?: string
      pix_provider_tid?: string
      gateway_id?: string
      created_at: string
      updated_at: string
      gateway_response?: {
        code: string
        errors: Array<{ message: string }>
      }
    }
  }>
  documents: any[]
}

class PaymentService {
  /**
   * Gera uma cobrança PIX
   */
  async generatePixCharge(orderId: number, amount: number, applyDiscount = false): Promise<PixTransaction> {
    try {
      // Aplicar desconto PIX se habilitado
      let finalAmount = amount
      console.log('applyDiscount', applyDiscount)
      if (applyDiscount) {
        const applyPixDiscount = import.meta.env.VITE_APP_APPLY_PIX_DISCOUNT === 'enabled'
        const pixDiscountPercentage = Number(import.meta.env.VITE_APP_PIX_DISCOUNT_PERCENTAGE || 5)

        console.log('applyPixDiscount', applyPixDiscount)
        if (applyPixDiscount) {
          const discount = amount * (pixDiscountPercentage / 100)
          finalAmount = amount - discount
        }
      }

      const payload: PaymentChargePayload = {
        order_id: orderId,
        payments: [
          {
            paymentMethod: { type: 'pix' },
            amount: Math.round(finalAmount * 100) // Converte para centavos
          }
        ],
        utmParams: {}
      }

      console.log('payload', payload)

      const response = await api.post<PaymentResponse>('/payments/charges', payload)

      if (response.data.success && response.data.responses && response.data.responses.length > 0) {
        const paymentResponse = response.data.responses[0]

        if (paymentResponse.last_transaction && paymentResponse.last_transaction.success) {
          return {
            id: paymentResponse.id,
            gateway_id: paymentResponse.gateway_id || '',
            qr_code: paymentResponse.last_transaction.qr_code || '',
            qr_code_text: paymentResponse.last_transaction.qr_code_text || '',
            qr_code_url: paymentResponse.last_transaction.qr_code_url || '',
            expires_at: paymentResponse.last_transaction.expires_at || '',
            status: paymentResponse.last_transaction.status || 'pending',
            pix_provider_tid: paymentResponse.last_transaction.pix_provider_tid || ''
          }
        }

        // Se falhou, incluir detalhes do erro
        const errorDetails =
          paymentResponse.last_transaction?.gateway_response?.errors?.[0]?.message || 'Erro desconhecido'
        throw new Error(`Falha ao gerar PIX: ${errorDetails}`)
      } else {
        throw new Error('Resposta inválida da API')
      }
    } catch (error) {
      console.error('Erro ao gerar PIX:', error)
      throw error
    }
  }

  /**
   * Gera uma cobrança de boleto
   */
  async generateBoletoCharge(orderId: number, amount: number): Promise<BoletoTransaction> {
    try {
      const payload: PaymentChargePayload = {
        order_id: orderId,
        payments: [
          {
            paymentMethod: { type: 'boleto' },
            amount: Math.round(amount * 100) // Converte para centavos
          }
        ],
        utmParams: {}
      }

      // console.log('Gerando boleto com payload:', payload)
      const response = await api.post<PaymentResponse>('/payments/charges', payload)
      // console.log('Resposta da API boleto:', response.data)

      if (response.data.success && response.data.responses && response.data.responses.length > 0) {
        const paymentResponse = response.data.responses[0]

        if (paymentResponse.last_transaction && paymentResponse.last_transaction.success) {
          const lt = paymentResponse.last_transaction
          return {
            id: paymentResponse.id,
            gateway_id: paymentResponse.gateway_id || '',
            // Compat (antigos)
            boleto_barcode: lt.boleto_barcode || '',
            boleto_expiration_date: lt.boleto_expiration_date || lt.expires_at || lt.due_at || '',
            boleto_url: lt.boleto_url || lt.url || '',
            // Novos
            line: lt.line || '',
            pdf: lt.pdf || '',
            barcode: lt.barcode || '',
            url: lt.url || '',
            due_at: lt.due_at || lt.expires_at || '',
            status: lt.status || 'pending',
            qr_code: lt.qr_code,
            qr_code_url: lt.qr_code_url
          }
        }

        // Se falhou, incluir detalhes do erro
        const errorDetails =
          paymentResponse.last_transaction?.gateway_response?.errors?.[0]?.message || 'Erro desconhecido'
        throw new Error(`Falha ao gerar boleto: ${errorDetails}`)
      } else {
        throw new Error('Resposta inválida da API')
      }
    } catch (error) {
      console.error('Erro ao gerar boleto:', error)
      throw error
    }
  }

  /**
   * Verifica o status de um pagamento
   */
  async checkPaymentStatus(orderId: string | number, chargeId: string): Promise<any> {
    try {
      const response = await api.get(`/payments/charge/${orderId}/${chargeId}`)
      return response.data
    } catch (error) {
      console.error('Erro ao verificar status do pagamento:', error)
      throw error
    }
  }

  /**
   * Gera cobrança com cartão de crédito
   */
  async generateCreditCardCharge(orderId: number, cardData: any, amount: number): Promise<any> {
    try {
      const payload: PaymentChargePayload = {
        order_id: orderId,
        payments: [
          {
            paymentMethod: { type: 'credit_card' },
            card_number: cardData.card_number,
            card_name: cardData.card_name,
            card_month_validate: cardData.card_month_validate,
            card_year_validate: cardData.card_year_validate,
            card_cvv: cardData.card_cvv,
            installments: cardData.installments || 1,
            remeberCard: false,
            card_value: Math.round(amount * 100),
            amount: Math.round(amount * 100)
          }
        ],
        utmParams: {}
      }

      const response = await api.post<PaymentResponse>('/payments/charges', payload)
      return response.data
    } catch (error) {
      console.error('Erro ao gerar cobrança de cartão:', error)
      throw error
    }
  }

  /**
   * Gera cobrança usando cartão salvo (card_id + cvv)
   */
  async generateSavedCardCharge(
    orderId: number,
    cardExternalId: string,
    cvv: string,
    amount: number,
    installments = 1
  ): Promise<any> {
    try {
      const payload: PaymentChargePayload = {
        order_id: orderId,
        payments: [
          {
            paymentMethod: { type: 'credit_card' },
            card_id: cardExternalId,
            cvv,
            installments,
            card_value: Math.round(amount * 100),
            amount: Math.round(amount * 100)
          }
        ],
        utmParams: {}
      }

      const response = await api.post<PaymentResponse>('/payments/charges', payload)

      const { data } = response
      if (data.success && data.responses && data.responses.length > 0) {
        const paymentResponse = data.responses[0]
        if (paymentResponse.last_transaction && paymentResponse.last_transaction.success) {
          return data
        }

        const errorDetails =
          paymentResponse.last_transaction?.gateway_response?.errors?.[0]?.message || 'Erro desconhecido'
        throw new Error(`Falha ao processar pagamento: ${errorDetails}`)
      }

      throw new Error('Resposta inválida da API')
    } catch (error) {
      console.error('Erro ao gerar cobrança com cartão salvo:', error)
      throw error
    }
  }

  /**
   * Verifica elegibilidade do cliente para Pagaleve
   */
  async checkPagaleveEligibility(data: PagaleveEligibilityRequest): Promise<boolean> {
    try {
      // Validação básica dos dados
      if (!data.cpf || !data.amount || !data.phone || !data.email) {
        console.warn('Dados incompletos para verificação de elegibilidade Pagaleve')
        return false
      }

      // Limpar CPF e telefone
      const cleanCPF = data.cpf.replace(/\D/g, '')
      const cleanPhone = data.phone.replace(/\D/g, '')

      const payload = {
        cpf: cleanCPF,
        amount: data.amount,
        phone: cleanPhone,
        email: data.email
      }

      const response = await api.post<PagaleveEligibilityResponse>('/payments/pagaleve/eligibility', payload)

      if (response.data.success && response.data.data) {
        const isEligible = response.data.data.eligible === true
        console.log(`Cliente ${isEligible ? 'ELEGÍVEL' : 'NÃO ELEGÍVEL'} para Pagaleve`)
        return isEligible
      }

      return false
    } catch (error: any) {
      console.error('Erro ao verificar elegibilidade Pagaleve:', error?.response?.data || error.message)
      // Em caso de erro, não mostrar a opção (fail-safe)
      return false
    }
  }

  /**
   * Gera checkout Pagaleve (PIX parcelado)
   */
  async generatePagaleveCheckout(orderId: number): Promise<PagaleveCheckout> {
    try {
      const payload = {
        order_id: orderId,
        payment_gateway: 'pagaleve'
      }

      console.log('Gerando checkout Pagaleve:', payload)

      const response = await api.post('/payments/charges', payload)

      console.log('Resposta Pagaleve:', response.data)

      if (response.data.success && response.data.data) {
        return {
          checkout_id: response.data.data.checkout_id,
          checkout_url: response.data.data.checkout_url,
          qr_code: response.data.data.qr_code,
          qr_code_url: response.data.data.qr_code_url,
          status: response.data.data.status || 'pending',
          expires_at: response.data.data.expires_at,
          amount: response.data.data.amount
        }
      }

      throw new Error(response.data.message || 'Erro ao criar checkout Pagaleve')
    } catch (error: any) {
      console.error('Erro ao gerar checkout Pagaleve:', error)
      throw new Error(error?.response?.data?.message || error.message || 'Erro ao criar checkout Pagaleve')
    }
  }
}

export const paymentService = new PaymentService()
export default paymentService
